"use client";

import { BaseClickable, Body, Box, Divider, Heading } from "@biom3/react";
import { useEffect, useState } from "react";

import { LinkWallet } from "@/components/core/LinkWallet";
import { RoundedBox } from "@/components/core/RoundedBox";
import { Modal } from "@/components/modal";
import { ImportVsLinkInfo } from "@/components/wallets/ImportVsLinkInfo";
import { LinkWalletButton } from "@/components/wallets/LinkWalletButton";
import { LinkedWallets } from "@/components/wallets/LinkedWallet";
import { appConfig } from "@/constants";
import { WagmiProvider, usePassportProvider } from "@/context";
import { useFetchWallets } from "@/hooks";
import { type Wallet, WalletTypes } from "@/types";
import { analytics } from "@analytics";
import { Loader } from "@ui-kit/Loader";
import { AnimatedEmptyState } from "../emptystates/AnimatedEmptyState";

const ExternalWalletsComponent = ({
  onHowItWorks,
  isLoading,
  wallets = [],
}: {
  onHowItWorks: (trackEvent: boolean) => void;
  isLoading: boolean;
  wallets?: Wallet[];
}) => {
  const [isWalletConnectorsModalOpen, setIsWalletConnectorsModalOpen] =
    useState<boolean>(false);
  const { walletAddress } = usePassportProvider();

  if (isLoading) {
    return <Loader />;
  }

  return (
    <WagmiProvider>
      <RoundedBox testId="external-wallets-container">
        <LinkWallet
          isConnectorsModalOpen={isWalletConnectorsModalOpen}
          onCloseConnectorsModal={() => setIsWalletConnectorsModalOpen(false)}
          linkedWallets={wallets}
          onLinkPushPath={{ pathname: "/inventory" }}
          showImportOptions
          onLinkAnother={() => setIsWalletConnectorsModalOpen(true)}
          origin="ExternalWallets"
        />
        {!wallets || wallets.length === 0 ? (
          <AnimatedEmptyState
            creatingWallet={!walletAddress}
            rivePath={`${appConfig.ASSET_BASE_URL}/rive/wallets.riv`}
            riveStateMachine={"External Wallets"}
            riveInputPath={"Smiley - White"}
            titleKey={"wallets_empty_title_variation_b"}
            captionKey={"wallets_empty_caption_variation_b"}
            primaryButtonKey={"wallets_empty_primary_button_b"}
            secondaryButtonKey={"wallets_empty_secondary_button_b"}
            primaryButtonClick={() => {
              analytics.track(
                "PassportDashboardWalletsEmptyState_PrimaryButtonPressed",
              );
              setIsWalletConnectorsModalOpen(true);
            }}
            secondaryButtonClick={() => {
              analytics.track(
                "PassportDashboardWalletsEmptyState_SecondaryButtonPressed",
              );
              onHowItWorks(false);
            }}
          />
        ) : (
          <>
            <Heading size="small" sx={{ mb: "base.spacing.x4" }}>
              Got collectibles in another wallet?
            </Heading>
            <Box
              sx={{
                mb: "base.spacing.x12",
                width: "100%",
              }}
            >
              <Body>
                If you have Immutable X or Immutable zkEVM game items sitting in
                another wallet and want to use them in game, all you have to do
                is link your EOA wallet (or a hardware wallet).{" "}
                <Body
                  sx={{
                    color: "base.color.accent.1",
                    textDecoration: "none",
                  }}
                  rc={<BaseClickable onClick={() => onHowItWorks(true)} />}
                >
                  How it works
                </Body>
              </Body>
            </Box>

            <Divider
              size="small"
              sx={{
                mb: "base.spacing.x4",
                px: "base.spacing.x1",
                width: "100%",
                ":after": {
                  bg: "base.color.neutral.700",
                },
              }}
            />
            <LinkedWallets wallets={wallets} />
            <LinkWalletButton linkedWallets={wallets} />
          </>
        )}
      </RoundedBox>
    </WagmiProvider>
  );
};

const ExternalWalletsList = () => {
  const { data, error, isLoading } = useFetchWallets();
  const wallets =
    data?.filter((wallet: Wallet) => wallet.type !== WalletTypes.PASSPORT) ??
    [];
  const [isImportVsLinkOpen, setIsImportVsLinkOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!isLoading && (!wallets || wallets.length === 0)) {
      analytics.track("PassportDashboardWalletsEmptyState_ViewedEffect");
    }
  }, [wallets, isLoading]);

  return (
    <Box>
      <Modal open={isImportVsLinkOpen}>
        <Modal.Content>
          <ImportVsLinkInfo
            onBack={undefined}
            onClose={() => {
              setIsImportVsLinkOpen(false);
            }}
          />
        </Modal.Content>
      </Modal>
      <ExternalWalletsComponent
        {...{ error, isLoading, wallets }}
        onHowItWorks={(trackEvent: boolean) => {
          if (trackEvent) {
            analytics.track(
              "PassportDashboardWalletsExternalWallets_HowItWorksLinkPressed",
            );
          }
          setIsImportVsLinkOpen(true);
        }}
      />
    </Box>
  );
};

export { ExternalWalletsComponent, ExternalWalletsList };
